import React, {
  ChangeEvent,
  useCallback,
  useState,
  useEffect,
  useRef,
} from 'react';
import { DriverRegistrationPortalNoAuthService } from '@gen/driverRegistration/driverRegistrationPortalNoAuth';
import { FieldLabel } from './FieldLabel';
import ErrorHint from '../common/ErrorHint';
import FieldDescription from '../common/FieldDescription';
import { useSelector } from '../../redux/store';
import { getFieldError } from '../../redux/notification/notificationsSelectors';
import { getTranslations, getSteps } from '../../redux/form/formSelectors';
import {
  getCountry,
  getLanguage,
} from '../../redux/localization/localizationSelectors';
import { Select } from './Select';
import { Choice } from './SelectCommon';
import driverRegistrationApiClient from '../../api/DriverRegistrationApi';

const DEFAULT_STRUCTURED_ADDRESS_CONFIG: DriverRegistrationPortalNoAuthService.StructuredAddressFieldResponse[] =
  [
    { name: 'flat_number', label: '', required: false },
    { name: 'building_number', label: '', required: false },
    { name: 'street_name', label: '', required: false },
    { name: 'city_name', label: '', required: false },
    { name: 'postcode', label: '', required: false },
    { name: 'suburb', label: '', required: false },
    { name: 'region', label: '', required: false },
    { name: 'country_code', label: '', required: true },
  ];

interface StructuredAddressProps {
  field: DriverRegistrationPortalNoAuthService.FieldResponse;
  updateHandler: (
    changed: DriverRegistrationPortalNoAuthService.FieldResponse['current_value']
  ) => void;
  blurHandler: (
    changed: DriverRegistrationPortalNoAuthService.FieldResponse['current_value']
  ) => void;
}

export const StructuredAddress: React.FC<StructuredAddressProps> = ({
  field,
  updateHandler,
  blurHandler,
}) => {
  const commonTranslations = useSelector(getTranslations);
  const country = useSelector(getCountry);
  const currentLanguage = useSelector(getLanguage);
  const error = useSelector(getFieldError(field.name));
  const steps = useSelector(getSteps);

  const [countries, setCountries] = useState<Choice[]>([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);
  const [countriesError, setCountriesError] = useState<string | null>(null);
  const [localAddress, setLocalAddress] =
    useState<DriverRegistrationPortalNoAuthService.StructuredAddress | null>(
      null
    );
  const hasFocusRef = useRef(false);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const findFieldInAllSteps = useCallback(
    (fieldName: string) => {
      for (const step of steps) {
        const foundField = step.fields.find((f) => f.name === fieldName);
        if (foundField) {
          return foundField;
        }
      }
      return null;
    },
    [steps]
  );

  const billingAddressField = findFieldInAllSteps('structured_address');

  const fieldResponse = field;
  const addressConfig =
    fieldResponse.structured_address_config ??
    DEFAULT_STRUCTURED_ADDRESS_CONFIG;

  const getFieldConfig = useCallback(
    (fieldName: string) => {
      return addressConfig.find(
        (
          config: DriverRegistrationPortalNoAuthService.StructuredAddressFieldResponse
        ) => config.name === fieldName
      );
    },
    [addressConfig]
  );

  const getPlaceholder = useCallback(
    (fieldName: string) => {
      const config = getFieldConfig(fieldName);
      if (config && config.label) {
        return config.label;
      }

      const placeholderMap: Record<string, string> = {
        flat_number: commonTranslations.address.apartment_number_placeholder,
        building_number: commonTranslations.address.building_number_placeholder,
        street_name: commonTranslations.address.street_name_placeholder,
        city_name: commonTranslations.address.city_placeholder,
        postcode: commonTranslations.address.postcode_placeholder,
        suburb: commonTranslations.address.suburb_placeholder,
        region: commonTranslations.address.region_placeholder,
        country_code: isLoadingCountries
          ? commonTranslations.address.country_loading_placeholder
          : commonTranslations.address.country_select_placeholder,
      };

      return placeholderMap[fieldName] || '';
    },
    [getFieldConfig, commonTranslations, isLoadingCountries]
  );

  useEffect(() => {
    const fetchCountries = async () => {
      setIsLoadingCountries(true);
      setCountriesError(null);

      try {
        const response = await driverRegistrationApiClient.getAllCountries({
          language: currentLanguage || undefined,
        });

        const countryChoices: Choice[] = response.list.map((countryItem) => ({
          value: countryItem.value,
          title: countryItem.title,
        }));

        setCountries(countryChoices);
      } catch (fetchError) {
        console.error('Failed to fetch countries:', fetchError);
        setCountriesError(commonTranslations.address.countries_load_error);
        setCountries([]);
      } finally {
        setIsLoadingCountries(false);
      }
    };

    fetchCountries();
  }, [currentLanguage, commonTranslations.address.countries_load_error]);

  const currentAddress = React.useMemo(() => {
    const defaultAddress: DriverRegistrationPortalNoAuthService.StructuredAddress =
      {
        country_code: country || null,
        postcode: null,
        city_name: null,
        street_name: null,
        building_number: null,
        flat_number: null,
        suburb: null,
        fallback_address: '',
        region: null,
      };

    let parsedFieldValue: DriverRegistrationPortalNoAuthService.StructuredAddress | null =
      null;
    if (field.current_value) {
      try {
        if (typeof field.current_value === 'string') {
          parsedFieldValue = null; // We can't use strings, we want only an object
        } else {
          parsedFieldValue =
            field.current_value as DriverRegistrationPortalNoAuthService.StructuredAddress;
        }
      } catch (parseError) {
        console.error('Failed to parse field.current_value:', parseError);
        parsedFieldValue = null;
      }
    }

    const baseAddress = {
      ...defaultAddress,
      ...(parsedFieldValue || {}),
    };

    return localAddress || baseAddress;
  }, [field.current_value, country, localAddress]);

  const updateAddressField = useCallback(
    (
      fieldName: keyof DriverRegistrationPortalNoAuthService.StructuredAddress,
      value: string | null
    ) => {
      const updatedAddress = {
        ...currentAddress,
        [fieldName]: value,
      };
      setLocalAddress(updatedAddress);
    },
    [currentAddress]
  );

  const copyBillingAddress = useCallback(() => {
    if (billingAddressField?.current_value) {
      const billingAddress =
        billingAddressField.current_value as DriverRegistrationPortalNoAuthService.StructuredAddress;
      setLocalAddress(billingAddress);
      updateHandler(billingAddress);
    }
  }, [billingAddressField, updateHandler]);

  const handleFocus = useCallback(() => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    hasFocusRef.current = true;
  }, []);

  const handleBlur = useCallback(() => {
    blurTimeoutRef.current = setTimeout(() => {
      if (!hasFocusRef.current) {
        blurHandler(currentAddress);
        updateHandler(currentAddress);
      }
    }, 0);

    hasFocusRef.current = false;
  }, [blurHandler, updateHandler, currentAddress]);

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  const shouldShowCopyButton = field.name === 'structured_mailing_address';

  const billingAddressHasData =
    billingAddressField?.current_value &&
    Object.values(
      billingAddressField.current_value as DriverRegistrationPortalNoAuthService.StructuredAddress
    ).some((value) => value !== null && value !== '' && value !== undefined);

  const shouldShowFieldError = useCallback(
    (fieldName: string, value: string | null | undefined) => {
      const config = getFieldConfig(fieldName);
      if (!config || !config.required) {
        return false;
      }
      return error && (!value || value.trim() === '');
    },
    [getFieldConfig, error]
  );

  const getFieldClasses = useCallback(
    (fieldName: string, value: string | null | undefined) => {
      const hasError = shouldShowFieldError(fieldName, value);
      const borderColorClassName = hasError
        ? 'border-red-500'
        : 'border-gray-200 focus:border-green-500 dark:border-dark-input-background dark:focus:border-dark-green';
      const bgClassName = hasError
        ? 'bg-white dark:bg-dark-input-background'
        : 'bg-gray-200 dark:bg-dark-input-background dark:focus:bg-dark-background';
      return `outline-none h-12 p-3 border-2 placeholder-gray-700 dark:placeholder-dark-content-secondary rounded focus:bg-white ${borderColorClassName} ${bgClassName} w-full`;
    },
    [shouldShowFieldError]
  );

  const { translations, name, is_required } = field;
  const { label, description } = translations;

  const renderAddressField = useCallback(
    (
      fieldConfig: DriverRegistrationPortalNoAuthService.StructuredAddressFieldResponse
    ) => {
      const fieldName = fieldConfig.name;

      if (fieldName === 'country_code') {
        return (
          <div key={fieldName}>
            <Select
              title=""
              description=""
              placeholder={getPlaceholder(fieldName)}
              choices={countries}
              value={currentAddress.country_code}
              error={
                shouldShowFieldError(
                  'country_code',
                  currentAddress.country_code
                )
                  ? error
                  : undefined
              }
              updateHandler={(value) => {
                updateAddressField('country_code', value as string | null);
              }}
              blurHandler={handleBlur}
              isSearchable
              isRequired={fieldConfig.required}
              hideLabel
            />
            {countriesError && (
              <div className="text-red-500 text-sm mt-1">{countriesError}</div>
            )}
          </div>
        );
      }

      const fieldValue = currentAddress[
        fieldName as keyof typeof currentAddress
      ] as string | null | undefined;

      return (
        <div key={fieldName}>
          <input
            id={`${name}_${fieldName}`}
            data-test={`input_${name}_${fieldName}`}
            className={getFieldClasses(fieldName, fieldValue)}
            placeholder={getPlaceholder(fieldName)}
            value={fieldValue || ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              updateAddressField(
                fieldName as keyof DriverRegistrationPortalNoAuthService.StructuredAddress,
                e.target.value || null
              );
            }}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </div>
      );
    },
    [
      name,
      currentAddress,
      getFieldClasses,
      getPlaceholder,
      updateAddressField,
      handleFocus,
      handleBlur,
      countries,
      shouldShowFieldError,
      error,
      countriesError,
    ]
  );

  const shouldShowErrorHint = () => {
    return (
      error &&
      currentAddress.country_code &&
      currentAddress.country_code.trim() !== ''
    );
  };

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-2">
        <FieldLabel text={label} isRequired={is_required} />
        {shouldShowCopyButton && billingAddressHasData && (
          <button
            type="button"
            onClick={copyBillingAddress}
            className="text-green-600 hover:text-green-800 font-medium text-sm underline"
          >
            {commonTranslations.address.copy_from_billing_address}
          </button>
        )}
      </div>
      <div className="w-full my-2 space-y-3">
        {addressConfig.map((fieldConfig) => renderAddressField(fieldConfig))}
        {!addressConfig.some((config) => config.name === 'country_code') && (
          <div>
            <Select
              title=""
              description=""
              placeholder={
                isLoadingCountries
                  ? commonTranslations.address.country_loading_placeholder
                  : commonTranslations.address.country_select_placeholder
              }
              choices={countries}
              value={currentAddress.country_code}
              error={
                shouldShowFieldError(
                  'country_code',
                  currentAddress.country_code
                )
                  ? error
                  : undefined
              }
              updateHandler={(value) => {
                updateAddressField('country_code', value as string | null);
              }}
              blurHandler={handleBlur}
              isSearchable
              isRequired
              hideLabel
            />
            {countriesError && (
              <div className="text-red-500 text-sm mt-1">{countriesError}</div>
            )}
          </div>
        )}
      </div>
      {shouldShowErrorHint() && <ErrorHint error={error} />}
      <FieldDescription description={description} />
    </div>
  );
};
